import { ArchiveBoxArrowDownIcon, ArrowUturnUpIcon, BellAlertIcon, ClockIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PersonPlacementEvent } from "../../../api/threads/placementsApi";
import { formatWeekdayTime } from "../../../shared/formatting/dateTimes";
import AppButton from "../../../ui/buttons/AppButton";
import IconButton from "../../../ui/buttons/IconButton";
import PinIcon from "../../../ui/icons/PinIcon";
import Field from "../../../ui/forms/Field";
import ModalShell from "../../../ui/overlays/ModalShell";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import RenameThreadDialog from "../hosting/RenameThreadDialog";
import { snoozePresets, type InboxPlace } from "./inboxPlacement";

interface ThreadRowActionsProps {
  title: string;
  place: InboxPlace;
  /** Pinned by this person: the Pinned section holds it, unless it is snoozed or settled. */
  pinned: boolean;
  /** From the list line, by module C's rule: neither working nor waiting for a person's input. */
  canSettleOrSnooze: boolean;
  onPlace: (event: PersonPlacementEvent, snoozedUntil?: Date) => void;
  /** The viewer hosts it and it is open: they may rename it (F68), on the newest entry they listed. */
  rename: { readonly threadId: string; readonly lastEntryNumber: number } | null;
}

const ICON_CLASS = "h-3.5 w-3.5";
const BUTTON_CLASS = "h-5 w-5 p-0.5";

/**
 * A listed Thread's own buttons (F44), as T3 Code's row actions: Pin or Unpin; Settle or
 * Un-settle; and Snooze, which asks until when (T3 Code's choices or a time of one's own), or
 * Wake now. The choice is a dialog, so a narrow, scrolling sidebar never cuts it off. A
 * working Thread, or one waiting for input, offers neither Settle nor Snooze (Johannes,
 * 27 Oct; T3 Code); Pin, Un-settle and Wake stay. Pinning a settled Thread un-settles it.
 * Its host renames it here too (F68).
 */
export default function ThreadRowActions({ title, place, pinned, canSettleOrSnooze, onPlace, rename }: ThreadRowActionsProps) {
  const { t } = useTranslation();
  const [choosing, setChoosing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const renameButton =
    rename === null ? null : (
      <>
        <IconButton label={t("threads.rename.title")} title={t("threads.rename.title")} onClick={() => { setRenaming(true); }} className={BUTTON_CLASS}>
          <PencilSquareIcon className={ICON_CLASS} />
        </IconButton>
        {renaming ? <RenameThreadDialog threadId={rename.threadId} title={title} lastEntryNumber={rename.lastEntryNumber} onClose={() => { setRenaming(false); }} /> : null}
      </>
    );
  const pinLabel = t(pinned ? "threads.list.placement.unpin" : "threads.list.placement.pin");
  const pin = (
    <IconButton label={pinLabel} title={pinLabel} aria-pressed={pinned} onClick={() => { onPlace(pinned ? "unpin" : "pin"); }} className={BUTTON_CLASS}>
      <PinIcon className={ICON_CLASS} filled={pinned} />
    </IconButton>
  );

  if (place === "settled") {
    return (
      <>
        {renameButton}
        {pin}
        <IconButton label={t("threads.list.placement.unsettle")} title={t("threads.list.placement.unsettle")} onClick={() => { onPlace("unsettle"); }} className={BUTTON_CLASS}>
          <ArrowUturnUpIcon className={ICON_CLASS} />
        </IconButton>
      </>
    );
  }
  return (
    <>
      {renameButton}
      {pin}
      {place === "snoozed" ? (
        <IconButton label={t("threads.list.placement.wake")} title={t("threads.list.placement.wake")} onClick={() => { onPlace("wake"); }} className={BUTTON_CLASS}>
          <BellAlertIcon className={ICON_CLASS} />
        </IconButton>
      ) : canSettleOrSnooze ? (
        <IconButton label={t("threads.list.placement.snoozeMenu", { title })} title={t("threads.list.placement.snooze")} onClick={() => { setChoosing(true); }} className={BUTTON_CLASS}>
          <ClockIcon className={ICON_CLASS} />
        </IconButton>
      ) : null}
      {canSettleOrSnooze ? (
        <IconButton label={t("threads.list.placement.settle")} title={t("threads.list.placement.settleTitle")} onClick={() => { onPlace("settle"); }} className={BUTTON_CLASS}>
          <ArchiveBoxArrowDownIcon className={ICON_CLASS} />
        </IconButton>
      ) : null}
      {choosing ? (
        <SnoozeDialog
          title={title}
          onSnooze={(until) => { setChoosing(false); onPlace("snooze", until); }}
          onClose={() => { setChoosing(false); }}
        />
      ) : null}
    </>
  );
}

/**
 * Until when: T3 Code's choices, or a time of one's own in local time, later than now (the
 * server refuses others). A relative choice counts from the click, however long the dialog
 * stayed open.
 */
function SnoozeDialog({ title, onSnooze, onClose }: { title: string; onSnooze: (until: Date) => void; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [opened] = useState(() => new Date());
  const presets = snoozePresets(opened);
  const [value, setValue] = useState(() => localInputValue(presets.find((preset) => preset.key === "tomorrow")?.until ?? opened));
  const until = new Date(value);
  const valid = !Number.isNaN(until.getTime()) && until > opened;
  return (
    <ModalShell title={t("threads.list.placement.snoozeMenu", { title })} onClose={onClose} closeOnOutsideClick size="md">
      <div className="space-y-4">
        <div className="grid gap-2">
          {presets.map((preset) => (
            <AppButton
              key={preset.key}
              variant="secondary"
              className="justify-between"
              onClick={() => { onSnooze(snoozePresets(new Date()).find((fresh) => fresh.key === preset.key)?.until ?? preset.until); }}
            >
              <span>{t(`threads.list.placement.presets.${preset.key}`)}</span>
              <span className="app-text-muted text-xs">{formatWeekdayTime(preset.until.toISOString(), i18n.resolvedLanguage)}</span>
            </AppButton>
          ))}
        </div>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) onSnooze(until);
          }}
        >
          <Field label={t("threads.list.placement.custom")} className="min-w-0 flex-1">
            {(control) => (
              <input {...control} type="datetime-local" value={value} min={localInputValue(opened)} onChange={(event) => { setValue(event.target.value); }} className={FORM_CONTROL_CLASS} />
            )}
          </Field>
          <AppButton type="submit" variant="primary" disabled={!valid}>{t("threads.list.placement.snooze")}</AppButton>
        </form>
      </div>
    </ModalShell>
  );
}

/** `datetime-local`'s value for `moment` in local time, to the minute. */
function localInputValue(moment: Date): string {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}T${pad(moment.getHours())}:${pad(moment.getMinutes())}`;
}
